import { useEffect, useState } from 'react'
import api from '../../services/api'
import { useTheme } from '../../hooks/useTheme.jsx'
import { formatDate, formatDateTime, parseServerDate } from '../../utils/helpers'

/**
 * What was deleted, and the button that puts it back.
 *
 * Deleting a project used to destroy rows across 24 tables and erase the stored files, with no
 * copy anywhere — dummy projects cleared on 20 August are gone for that reason, and the same
 * button on a real project would have taken a real one.
 *
 * Two things this screen is careful about:
 *
 * It never restores blind. Every entry shows what it holds, table by table, before you press
 * anything, because "restore" on something you cannot see is a guess.
 *
 * It does not overstate itself. Deletes are recoverable; EDITS are not. Someone who believes
 * this screen can undo a changed budget will not go looking for the real answer until it is too
 * late, so the page says so in plain words rather than implying an undo button for everything.
 */
export default function RecycleBin() {
  const { isDark } = useTheme()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(null)          // bin id currently being acted on
  const [open, setOpen] = useState(null)          // bin id whose contents are expanded
  const [detail, setDetail] = useState({})        // bin id → contents
  const [notice, setNotice] = useState(null)
  const [showRestored, setShowRestored] = useState(false)

  const load = async () => {
    setError(null)
    try {
      const { data } = await api.get('/recycle-bin/', { params: { include_restored: showRestored } })
      setData(data)
    } catch (err) {
      setError(err.response?.status === 403
        ? 'Only an Admin or Super Admin can open the recycle bin.'
        : 'Could not load the recycle bin.')
    }
  }

  useEffect(() => { load() }, [showRestored])     // eslint-disable-line react-hooks/exhaustive-deps

  const expand = async (id) => {
    if (open === id) { setOpen(null); return }
    setOpen(id)
    if (detail[id]) return
    try {
      const { data } = await api.get(`/recycle-bin/${id}`)
      setDetail(prev => ({ ...prev, [id]: data }))
    } catch {
      setDetail(prev => ({ ...prev, [id]: { contents: [], error: true } }))
    }
  }

  const restore = async (item) => {
    // Named in the prompt, with the record count, because "are you sure?" tells you nothing
    // about what is about to happen.
    if (!window.confirm(
      `Restore "${item.label}"?\n\n` +
      `${item.row_count} record(s) and ${item.blob_count} file(s) will be put back with their ` +
      `original IDs, so numbers and links are preserved.`)) return
    setBusy(item.id); setNotice(null)
    try {
      const { data } = await api.post(`/recycle-bin/${item.id}/restore`)
      const partial = data.files_restored < data.files_expected
      setNotice({ kind: partial ? 'warn' : 'ok', text: `${data.detail} Refreshing so it appears everywhere…` })
      await load()
      // A restore writes rows back across two dozen tables, so every list already on screen —
      // projects, dashboard counts, contracts, the invoice queue — is now out of date. Reloading
      // the bin alone left the restored project invisible until somebody pressed F5 and wondered
      // whether the restore had worked at all.
      //
      // Long enough to read the message, short enough not to feel stuck. Not done when files are
      // missing: that notice is the one worth staying on screen.
      if (!partial) setTimeout(() => window.location.reload(), 1400)
    } catch (err) {
      setNotice({ kind: 'error', text: err.response?.data?.detail || 'The restore failed.' })
    } finally {
      setBusy(null)
    }
  }

  const purge = async (item) => {
    // Two steps, and the second asks for the name typed out. This is the only irreversible
    // action on the page, so it should be harder than the reversible one.
    if (!window.confirm(
      `Permanently delete "${item.label}"?\n\n` +
      `This destroys the saved copy and its ${item.blob_count} file(s). It CANNOT be undone.`)) return
    const typed = window.prompt(`Type DELETE to confirm destroying "${item.label}" for good:`)
    if (typed !== 'DELETE') { setNotice({ kind: 'ok', text: 'Nothing was deleted.' }); return }
    setBusy(item.id); setNotice(null)
    try {
      const { data } = await api.delete(`/recycle-bin/${item.id}`)
      setNotice({ kind: 'ok', text: data.detail })
      await load()
    } catch (err) {
      setNotice({ kind: 'error', text: err.response?.data?.detail || 'The purge failed.' })
    } finally {
      setBusy(null)
    }
  }

  const card = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
  const primary = isDark ? 'text-white' : 'text-gray-900'
  const secondary = isDark ? 'text-gray-400' : 'text-gray-500'

  const ICON = {
    project: '📁', user: '👤', milestone: '🎯', deliverable: '📦', document: '📄',
  }

  // parseServerDate, not new Date(). The server sends a naive timestamp with no zone and
  // new Date() reads it as LOCAL, so a project deleted seconds ago reported "6 h ago" here —
  // 5.5 hours adrift in India. On this screen that is worse than untidy: how long ago something
  // was deleted is exactly what somebody uses to decide whether this is the deletion they are
  // looking for before pressing Restore.
  const when = (iso) => {
    const d = parseServerDate(iso)
    if (!d) return ''
    const mins = Math.round((Date.now() - d.getTime()) / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins} min ago`
    if (mins < 1440) return `${Math.round(mins / 60)} h ago`
    return formatDateTime(iso)
  }

  return (
    <div className={`min-h-full p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-4xl mx-auto space-y-5">

        <div>
          <h1 className={`text-2xl font-bold ${primary}`}>Recycle bin</h1>
          <p className={`text-sm mt-1 ${secondary}`}>
            Things that were deleted, and what it takes to put them back.
          </p>
        </div>

        {/* Said up front, not in a footnote. Somebody who thinks this undoes everything will not
            go looking for the real answer until it is too late to have one. */}
        <div className={`rounded-xl border px-4 py-3 text-sm ${isDark
          ? 'bg-amber-950/30 border-amber-900 text-amber-200'
          : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
          <strong>Deleted items only.</strong> Changes are not undoable from here — an edited
          budget or a reassigned consultant cannot be rolled back on this screen. Nightly database
          backups cover that; ask before relying on either.
        </div>

        {notice && (
          <div className={`rounded-xl border px-4 py-3 text-sm ${
            notice.kind === 'error'
              ? 'bg-red-50 border-red-200 text-red-800'
              : notice.kind === 'warn'
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-green-50 border-green-200 text-green-800'}`}>
            {notice.text}
          </div>
        )}

        {error && (
          <div className={`rounded-xl border p-6 ${card}`}>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {!data && !error && <p className={`text-sm ${secondary}`}>Loading…</p>}

        {data && (
          <>
            <label className={`flex items-center gap-2 text-sm ${secondary}`}>
              <input
                type="checkbox"
                checked={showRestored}
                onChange={(e) => setShowRestored(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Also show items already restored
            </label>

            {data.items.length === 0 ? (
              <div className={`rounded-xl border p-8 text-center ${card}`}>
                <p className={`text-sm ${secondary}`}>
                  Nothing here. Anything deleted from now on will appear on this page.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.items.map(item => (
                  <div key={item.id} className={`rounded-xl border ${card}`}>
                    <div className="p-4 flex items-start gap-3">
                      <span className="text-xl leading-none mt-0.5">{ICON[item.entity_type] || '🗑️'}</span>

                      <div className="min-w-0 flex-1">
                        <p className={`font-medium truncate ${primary}`}>{item.label}</p>
                        <p className={`text-xs mt-0.5 ${secondary}`}>
                          {item.entity_type} · deleted {when(item.deleted_at)}
                          {item.deleted_by ? ` by ${item.deleted_by}` : ''}
                        </p>
                        <p className={`text-xs mt-1 ${secondary}`}>
                          {item.row_count} record{item.row_count === 1 ? '' : 's'}
                          {item.blob_count > 0 && ` · ${item.blob_count} file${item.blob_count === 1 ? '' : 's'}`}
                          {item.summary && ` — ${item.summary}`}
                        </p>
                        {item.expires_on && !item.restored_at && (
                          <p className="text-xs mt-1 text-amber-600">
                            Restorable until {formatDate(item.expires_on)}, then this person and their saved files are destroyed.
                          </p>
                        )}
                        {item.restored_at && (
                          <p className="text-xs mt-1 text-green-600">
                            Restored {when(item.restored_at)}
                            {item.restored_by ? ` by ${item.restored_by}` : ''}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => expand(item.id)}
                          className="text-xs text-blue-600 hover:underline mt-1.5"
                        >
                          {open === item.id ? 'Hide what is inside' : 'See exactly what comes back'}
                        </button>
                      </div>

                      {!item.restored_at && (
                        <div className="flex flex-col gap-1.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => restore(item)}
                            disabled={busy === item.id}
                            className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-40"
                          >
                            {busy === item.id ? 'Working…' : 'Restore'}
                          </button>
                          <button
                            type="button"
                            onClick={() => purge(item)}
                            disabled={busy === item.id}
                            className="px-3 py-1.5 rounded-lg text-xs text-red-600 hover:bg-red-50 disabled:opacity-40"
                          >
                            Delete for good
                          </button>
                        </div>
                      )}
                    </div>

                    {open === item.id && (
                      <div className={`px-4 pb-4 pt-1 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                        {!detail[item.id] ? (
                          <p className={`text-xs pt-3 ${secondary}`}>Reading the snapshot…</p>
                        ) : detail[item.id].error ? (
                          <p className="text-xs pt-3 text-red-600">Could not read the snapshot.</p>
                        ) : (
                          <div className="pt-3 space-y-3">
                            <table className="text-xs w-full">
                              <tbody>
                                {(detail[item.id].contents || []).map(c => (
                                  <tr key={c.table}>
                                    <td className={`py-0.5 pr-4 ${secondary}`}>{c.table.replace(/_/g, ' ')}</td>
                                    <td className={`py-0.5 tabular-nums ${primary}`}>{c.rows}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {(detail[item.id].relinks || []).length > 0 && (
                              <p className={`text-xs ${secondary}`}>
                                Also re-attached:{' '}
                                {detail[item.id].relinks
                                  .map(r => `${r.rows} ${r.table.replace(/_/g, ' ')}`)
                                  .join(', ')}
                                {' '}— those rows were never deleted, only detached.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <p className={`text-xs ${secondary}`}>
              Covers: {(data.covers || []).join(', ')}. Restoring keeps the original IDs, so
              project numbers, contract references and links survive.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
